import { useState, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, DollarSign, Lock, Unlock, Check, X, Calendar, Trash2, Eye, FileText, StickyNote, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import ConfirmModal from "../ui/ConfirmModal";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function generateMonthDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startWeekDay = firstDay.getDay();
  const adjustedStart = startWeekDay === 0 ? 6 : startWeekDay - 1;
  
  const days = [];
  for (let i = 0; i < adjustedStart; i++) {
    days.push({ day: null, date: null, padding: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    days.push({ day: d, date: dateStr, padding: false });
  }
  return days;
}

const MONTH_NAMES = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", 
                    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEKDAYS = ["Lu", "Ma", "Me", "Gi", "Ve", "Sa", "Do"];

// Range rolling 10 anni per Admin (calcolato runtime)
const ADMIN_YEARS_RANGE = 10;

export default function PriceCalendarGrid({ 
  prices = {},
  blockedDates = [],
  bookings = [],
  onPriceChange,
  onBlockDates,
  onUnblockDates,
  onConfirmDates,
  onRemoveConfirmation,
  onCancelBooking,
  onNotesUpdate,
  onResolveOverbooking,
  getAuthHeaders,
  bookingWindowMonths = 12,
  currentYear: externalYear,
  onYearChange,
  readOnly = false,
  overbookingDates = []
}) {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const todayStr = today.toISOString().split('T')[0];
  
  // Range rolling 10 anni: today - 10 → today + 10 (calcolato runtime)
  const minAdminYear = currentYear - ADMIN_YEARS_RANGE;
  const maxAdminYear = currentYear + ADMIN_YEARS_RANGE;
  
  // Stato anno visualizzato
  const [internalYear, setInternalYear] = useState(currentYear);
  const displayYear = externalYear || internalYear;
  const setDisplayYear = onYearChange || setInternalYear;
  
  const [selectedDates, setSelectedDates] = useState([]);
  const [priceInput, setPriceInput] = useState("");
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [confirmNameInput, setConfirmNameInput] = useState(""); // Nome per conferma manuale
  const [detailsBooking, setDetailsBooking] = useState(null); // Booking da mostrare nei dettagli
  
  // State for Notes modal
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesInput, setNotesInput] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  
  // State for Confirm Modal
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    details: null,
    variant: 'danger',
    confirmText: 'Conferma',
    cancelText: 'Annulla',
    onConfirm: null
  });
  
  // Ref per gestire drag
  const dragRef = useRef({ isDragging: false, startDate: null });
  
  // === FUNZIONI HELPER ===
  
  const isManuallyBlocked = useCallback((dateStr) => {
    // Include manual blocks AND orphaned payment_locks (incomplete payments)
    return blockedDates.some(b => b.date === dateStr && (b.source === "manual" || b.source === "payment_lock"));
  }, [blockedDates]);
  
  const isICalBlocked = useCallback((dateStr) => {
    return blockedDates.some(b => b.date === dateStr && (b.source === "ical" || b.source === "channel_manager"));
  }, [blockedDates]);
  
  const isBlocked = useCallback((dateStr) => {
    return blockedDates.some(b => b.date === dateStr);
  }, [blockedDates]);
  
  const isPast = useCallback((dateStr) => {
    return dateStr < todayStr;
  }, [todayStr]);
  
  // Check if date has overbooking
  const hasOverbooking = useCallback((dateStr) => {
    return overbookingDates.includes(dateStr);
  }, [overbookingDates]);
  
  // Check if a booking is an iCal booking
  const isICalBooking = useCallback((booking) => {
    return booking && (booking.status === "ical" || booking.is_ical === true || booking.source === "ical");
  }, []);
  
  const getBookingForDate = useCallback((dateStr) => {
    return bookings.find(b => {
      if (b.name === "Manual Block") return false;
      // Include paid, confirmed AND ical bookings
      if (b.status !== "paid" && b.status !== "confirmed" && b.status !== "ical") return false;
      return dateStr >= b.check_in && dateStr < b.check_out;
    });
  }, [bookings]);
  
  // Conta quante prenotazioni occupano un giorno specifico (per questa risorsa)
  // Se > 1, significa che ci sono sovrapposizioni
  const getBookingsCountForDate = useCallback((dateStr) => {
    return bookings.filter(b => {
      if (b.name === "Manual Block") return false;
      if (b.status !== "paid" && b.status !== "confirmed" && b.status !== "ical") return false;
      return dateStr >= b.check_in && dateStr < b.check_out;
    }).length;
  }, [bookings]);
  
  const isCheckOutDay = useCallback((dateStr) => {
    // Il triangolo viola deve apparire per check_out di paid, confirmed E ical
    return bookings.some(b => 
      b.name !== "Manual Block" &&
      (b.status === "paid" || b.status === "confirmed" || b.status === "ical") && 
      b.check_out === dateStr
    );
  }, [bookings]);
  
  // Funzione per contare quanti check-out ci sono in un giorno
  const getCheckOutCount = useCallback((dateStr) => {
    return bookings.filter(b => 
      b.name !== "Manual Block" &&
      (b.status === "paid" || b.status === "confirmed" || b.status === "ical") && 
      b.check_out === dateStr
    ).length;
  }, [bookings]);
  
  // Verifica se una data è il giorno di CHECK-IN di una prenotazione
  const isCheckInDay = useCallback((dateStr) => {
    return bookings.some(b => 
      b.name !== "Manual Block" &&
      (b.status === "paid" || b.status === "confirmed" || b.status === "ical") && 
      b.check_in === dateStr
    );
  }, [bookings]);
  
  // Ottiene la prenotazione che ha CHECK-IN in questa data
  const getBookingWithCheckIn = useCallback((dateStr) => {
    return bookings.find(b => 
      b.name !== "Manual Block" &&
      (b.status === "paid" || b.status === "confirmed" || b.status === "ical") && 
      b.check_in === dateStr
    );
  }, [bookings]);
  
  // Formatta il numero ospiti secondo le regole:
  // - Adulti + Bambini sommati
  // - Neonati mostrati dopo "+" solo se > 0
  const formatGuestCount = useCallback((booking) => {
    if (!booking) return "";
    
    const adults = booking.adults || 0;
    const children = booking.children || 0;
    const infants = booking.infants || 0;
    
    const mainCount = adults + children;
    
    if (infants > 0) {
      return `(${mainCount}+${infants})`;
    }
    return `(${mainCount})`;
  }, []);
  
  // Formatta nome + ospiti + importo per visualizzazione nel calendario
  // Solo per il giorno di check-in
  // Per prenotazioni PAID: aggiunge l'importo totale
  const formatBookingLabel = useCallback((booking) => {
    if (!booking) return "";
    
    // Estrai solo il nome (prima parola)
    const firstName = booking.name?.split(" ")[0] || "";
    
    // Se è una conferma manuale con testo già formattato (contiene parentesi),
    // usa il testo così com'è
    if (booking.is_manual && booking.name?.includes("(")) {
      return booking.name;
    }
    
    // Formatta nome + ospiti
    const guestCount = formatGuestCount(booking);
    let label = `${firstName} ${guestCount}`;
    
    // Per prenotazioni PAID: aggiungi importo totale se disponibile
    if (booking.status === "paid" && booking.grand_total > 0) {
      const amount = Math.round(booking.grand_total);
      label += ` · €${amount}`;
    }
    
    return label;
  }, [formatGuestCount]);

  // Admin può selezionare qualsiasi data FUTURA (non passata)
  const canSelect = useCallback((dateStr) => {
    if (!dateStr || isPast(dateStr)) return false;
    return true;
  }, [isPast]);
  
  // Calcola range di date
  const getDateRange = (start, end) => {
    const startD = start < end ? start : end;
    const endD = start > end ? start : end;
    const range = [];
    let current = new Date(startD);
    const endDate = new Date(endD);
    while (current <= endDate) {
      const ds = current.toISOString().split("T")[0];
      if (!isPast(ds)) range.push(ds);
      current.setDate(current.getDate() + 1);
    }
    return range;
  };
  
  // === EVENT HANDLERS ===
  
  // Flag per tracciare se è un dispositivo touch
  const isTouchInteraction = useRef(false);
  
  const handleMouseDown = (dateStr, e) => {
    if (readOnly) return;
    // Se è un'interazione touch, ignora il mouse
    if (isTouchInteraction.current) return;
    
    e.preventDefault();
    if (!canSelect(dateStr)) return;
    
    dragRef.current = { 
      isDragging: true, 
      startDate: dateStr, 
      moved: false,
      isMouseInteraction: true
    };
  };
  
  const handleMouseEnter = (dateStr) => {
    if (readOnly) return;
    if (isTouchInteraction.current) return;
    if (!dragRef.current.isDragging || !dragRef.current.isMouseInteraction) return;
    if (!canSelect(dateStr)) return;
    
    if (dateStr !== dragRef.current.startDate) {
      dragRef.current.moved = true;
      const range = getDateRange(dragRef.current.startDate, dateStr);
      setSelectedDates(range);
      setShowActionPanel(true);
    }
  };
  
  const handleMouseUp = (dateStr, e) => {
    if (readOnly) return;
    if (isTouchInteraction.current) return;
    if (!dragRef.current.isMouseInteraction) return;
    
    const wasDrag = dragRef.current.moved;
    const startDate = dragRef.current.startDate;
    dragRef.current.isDragging = false;
    dragRef.current.isMouseInteraction = false;
    
    // Se non c'è stato drag, è un click - toggle
    if (!wasDrag && startDate && canSelect(startDate)) {
      // Shift+click per estendere range
      if (e.shiftKey && selectedDates.length >= 1) {
        const firstSelected = selectedDates[0];
        if (firstSelected !== startDate) {
          const range = getDateRange(firstSelected, startDate);
          setSelectedDates(range);
          setShowActionPanel(true);
        }
        return;
      }
      
      // Toggle singola data
      if (selectedDates.includes(startDate)) {
        const newSelection = selectedDates.filter(d => d !== startDate);
        setSelectedDates(newSelection);
        if (newSelection.length === 0) {
          setShowActionPanel(false);
        }
      } else {
        setSelectedDates([...selectedDates, startDate].sort());
        setShowActionPanel(true);
      }
    }
    
    dragRef.current.moved = false;
  };
  
  const handleTouchStart = (dateStr, e) => {
    // Marca che stiamo usando touch
    isTouchInteraction.current = true;
    
    if (readOnly) {
      const booking = getBookingForDate(dateStr);
      if (booking) {
        setDetailsBooking(booking);
      }
      return;
    }
    if (!canSelect(dateStr)) return;
    
    dragRef.current = {
      isDragging: true,
      startDate: dateStr,
      moved: false,
      isTouchInteraction: true
    };
  };
  
  const handleTouchMove = (e) => {
    if (readOnly) return;
    if (!dragRef.current.isDragging || !dragRef.current.isTouchInteraction) return;
    
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const dateStr = element?.dataset?.date;
    
    if (dateStr && canSelect(dateStr) && dateStr !== dragRef.current.startDate) {
      dragRef.current.moved = true;
      const range = getDateRange(dragRef.current.startDate, dateStr);
      setSelectedDates(range);
      setShowActionPanel(true);
    }
  };
  
  const handleTouchEnd = () => {
    if (readOnly) return;
    if (!dragRef.current.isTouchInteraction) return;
    
    const wasDrag = dragRef.current.moved;
    const dateStr = dragRef.current.startDate;
    dragRef.current.isDragging = false;
    dragRef.current.isTouchInteraction = false;
    
    // Se non c'è stato movimento, è un tap - toggle la data
    if (!wasDrag && dateStr && canSelect(dateStr)) {
      if (selectedDates.includes(dateStr)) {
        const newSelection = selectedDates.filter(d => d !== dateStr);
        setSelectedDates(newSelection);
        if (newSelection.length === 0) {
          setShowActionPanel(false);
        }
      } else {
        setSelectedDates([...selectedDates, dateStr].sort());
        setShowActionPanel(true);
      }
    }
    
    dragRef.current.moved = false;
  };
  
  // Handler per click - solo per readOnly mode e booking details
  const handleClick = (dateStr) => {
    if (!readOnly) return; // In edit mode, gestito da mouse/touch handlers
    
    const booking = getBookingForDate(dateStr);
    if (booking) {
      setDetailsBooking(booking);
    }
  };
  
  // === ACTIONS ===
  
  const applyPrice = () => {
    const price = parseFloat(priceInput);
    if (isNaN(price) || price < 0 || selectedDates.length === 0) return;
    onPriceChange?.(selectedDates, price);
    setPriceInput("");
  };
  
  const blockSelected = () => {
    const datesToBlock = selectedDates.filter(d => !isBlocked(d) && !getBookingForDate(d));
    if (datesToBlock.length > 0) onBlockDates?.(datesToBlock);
  };
  
  const unblockSelected = () => {
    const datesToUnblock = selectedDates.filter(d => isManuallyBlocked(d));
    if (datesToUnblock.length > 0) onUnblockDates?.(datesToUnblock);
  };
  
  const confirmSelected = () => {
    if (confirmNameInput.trim()) {
      onConfirmDates?.(selectedDates, confirmNameInput.trim());
      setConfirmNameInput("");
    }
  };
  
  const removeConfirmation = () => {
    onRemoveConfirmation?.(selectedDates);
  };
  
  const cancelBookingsInSelection = () => {
    // Usa allBookingsInSelection per eliminare TUTTE le prenotazioni selezionate
    if (allBookingsInSelection.size > 0) {
      const count = allBookingsInSelection.size;
      const bookingNames = Array.from(allBookingsInSelection).map(id => {
        const booking = bookings.find(b => b.request_id === id);
        return booking?.name || 'Prenotazione';
      }).join(', ');
      
      setConfirmModal({
        isOpen: true,
        title: 'Elimina Prenotazione',
        message: count === 1 
          ? 'Sei sicuro di voler eliminare questa prenotazione?' 
          : `Sei sicuro di voler eliminare ${count} prenotazioni?`,
        details: bookingNames,
        variant: 'danger',
        confirmText: 'Elimina',
        cancelText: 'Annulla',
        onConfirm: () => {
          onCancelBooking?.(Array.from(allBookingsInSelection));
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      });
    }
  };
  
  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };
  
  const clearSelection = () => {
    setSelectedDates([]);
    setShowActionPanel(false);
    setPriceInput("");
    setConfirmNameInput("");
    dragRef.current = { isDragging: false, startDate: null };
  };
  
  // === NOTES FUNCTIONS ===
  
  const openNotesModal = (booking) => {
    setNotesInput(booking?.notes || "");
    setShowNotesModal(true);
  };
  
  const saveNotes = async () => {
    if (!selectedBookingForNotes) return;
    setSavingNotes(true);
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      await axios.put(
        `${API}/admin/booking/${selectedBookingForNotes.request_id}/notes`,
        { notes: notesInput },
        headers
      );
      toast.success("Note salvate");
      setShowNotesModal(false);
      onNotesUpdate?.();
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Errore nel salvare le note");
    } finally {
      setSavingNotes(false);
    }
  };
  
  const deleteNotes = async () => {
    if (!selectedBookingForNotes) return;
    setSavingNotes(true);
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      await axios.delete(
        `${API}/admin/booking/${selectedBookingForNotes.request_id}/notes`,
        headers
      );
      toast.success("Note rimosse");
      setShowNotesModal(false);
      setNotesInput("");
      onNotesUpdate?.();
    } catch (error) {
      console.error("Error deleting notes:", error);
      toast.error("Errore nel rimuovere le note");
    } finally {
      setSavingNotes(false);
    }
  };
  
  // === NAVIGAZIONE ANNO ===
  
  const goToPrevYear = () => {
    if (displayYear > minAdminYear) {
      setDisplayYear(displayYear - 1);
    }
  };
  
  const goToNextYear = () => {
    if (displayYear < maxAdminYear) {
      setDisplayYear(displayYear + 1);
    }
  };
  
  const goToCurrentYear = () => {
    setDisplayYear(currentYear);
  };
  
  const canGoPrev = displayYear > minAdminYear;
  const canGoNext = displayYear < maxAdminYear;
  
  // === VERIFICA SELEZIONE ===
  
  const hasManuallyBlockedInSelection = selectedDates.some(d => isManuallyBlocked(d));
  const hasICalBlockedInSelection = selectedDates.some(d => isICalBlocked(d));
  const hasAvailableInSelection = selectedDates.some(d => !isBlocked(d) && !getBookingForDate(d));
  
  // Check if selection has overbooking
  const hasOverbookingInSelection = selectedDates.some(d => hasOverbooking(d));

  const hasRealBookingsInSelection = selectedDates.some(d => {
    const booking = getBookingForDate(d);
    return booking && booking.name !== "Conferma Manuale";
  });
  const hasManualConfirmationsInSelection = selectedDates.some(d => {
    const booking = getBookingForDate(d);
    return booking && booking.name === "Conferma Manuale";
  });
  
  // Check if selection has iCal bookings
  const hasICalBookingsInSelection = selectedDates.some(d => {
    const booking = getBookingForDate(d);
    return booking && isICalBooking(booking);
  });
  
  // Qualsiasi prenotazione (paid, confirmed o ical) nella selezione
  const hasAnyBookingInSelection = selectedDates.some(d => {
    const booking = getBookingForDate(d);
    return booking && (booking.status === "paid" || booking.status === "confirmed" || booking.status === "ical");
  });
  
  const bookingsInSelection = new Set();
  selectedDates.forEach(d => {
    const booking = getBookingForDate(d);
    if (booking && booking.name !== "Conferma Manuale") {
      bookingsInSelection.add(booking.request_id);
    }
  });
  
  // Tutte le prenotazioni (per il pulsante elimina) - esclude iCal (non eliminabili)
  const allBookingsInSelection = new Set();
  selectedDates.forEach(d => {
    const booking = getBookingForDate(d);
    if (booking && (booking.status === "paid" || booking.status === "confirmed") && !isICalBooking(booking)) {
      allBookingsInSelection.add(booking.request_id);
    }
  });
  
  // Tutte le prenotazioni per Note (include iCal)
  const allBookingsForNotes = new Set();
  selectedDates.forEach(d => {
    const booking = getBookingForDate(d);
    if (booking && (booking.status === "paid" || booking.status === "confirmed" || booking.status === "ical")) {
      allBookingsForNotes.add(booking.request_id);
    }
  });
  
  const manualConfirmationsInSelection = new Set();
  selectedDates.forEach(d => {
    const booking = getBookingForDate(d);
    if (booking && booking.name === "Conferma Manuale") {
      manualConfirmationsInSelection.add(booking.request_id);
    }
  });
  
  // Get selected booking (for notes)
  const getSelectedBooking = () => {
    if (allBookingsForNotes.size === 1) {
      const bookingId = Array.from(allBookingsForNotes)[0];
      return bookings.find(b => b.request_id === bookingId);
    }
    return null;
  };
  
  const selectedBookingForNotes = getSelectedBooking();
  
  // === RENDER MONTH (Layout Verticale Grande) ===
  
  const renderMonth = (year, month, isLast = false) => {
    const days = generateMonthDays(year, month);
    const monthKey = `${year}-${month}`;
    const isCurrentMonth = year === currentYear && month === currentMonth;
    
    return (
      <div 
        key={monthKey}
        className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm"
      >
        {/* Month Header - Large and Clear */}
        <div className={`${isCurrentMonth ? 'bg-[#D4AF37]' : 'bg-gradient-to-r from-[#1C1917] to-[#44403C]'} text-white p-4 rounded-t-xl`}>
          <h3 className="font-heading text-xl md:text-2xl text-center">
            {MONTH_NAMES[month]} {year}
          </h3>
        </div>
        
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 border-b border-[#E7E5E4] bg-[#F9F8F6]">
          {WEEKDAYS.map(wd => (
            <div key={wd} className="text-sm font-medium text-[#1C1917] text-center py-3">
              {wd}
            </div>
          ))}
        </div>
        
        {/* Days Grid - Large cells for easy interaction */}
        <div className="grid grid-cols-7 p-2 gap-1">
          {days.map((d, idx) => {
            if (d.padding) {
              return <div key={`pad-${idx}`} className="h-16 md:h-20" />;
            }
            
            const dateStr = d.date;
            const price = prices[dateStr];
            const blocked = isBlocked(dateStr);
            const past = isPast(dateStr);
            const selected = selectedDates.includes(dateStr);
            const booking = getBookingForDate(dateStr);
            const isCheckOut = isCheckOutDay(dateStr);
            const bookingsCount = getBookingsCountForDate(dateStr);
            const hasOverlap = bookingsCount > 1;
            const isIcal = booking && isICalBooking(booking);
            const hasNotes = booking && booking.notes && booking.notes.trim().length > 0;
            
            // In readOnly mode, all bookings are clickable to view details
            const isClickableInReadOnly = readOnly && booking;
            
            let cellClass = "h-16 md:h-20 rounded-lg text-center transition-all flex flex-col items-center justify-center relative select-none touch-manipulation ";
            let statusIndicator = null;
            
            if (past) {
              // DATE PASSATE: grigie, non editabili ma con storico visibile
              cellClass += "bg-gray-100 text-gray-400 cursor-default";
            } else if (selected) {
              cellClass += "bg-[#D4AF37] text-white ring-2 ring-[#D4AF37] ring-offset-2 cursor-pointer scale-105 shadow-lg z-10";
            } else if (booking) {
              // In readOnly, tutte le prenotazioni sono cliccabili per vedere i dettagli
              if (isClickableInReadOnly) {
                cellClass += "cursor-pointer hover:ring-2 hover:ring-[#D4AF37] hover:ring-offset-1 ";
              } else if (readOnly) {
                cellClass += "cursor-default ";
              } else {
                cellClass += "cursor-pointer ";
              }
              if (isIcal) {
                // iCal booking = GRIGIO
                cellClass += "bg-gray-400 text-white";
              } else if (booking.status === "paid") {
                cellClass += "bg-green-600 text-white";
              } else {
                // Confermato = CIANO per distinguerlo dal triangolo verde check-out
                cellClass += "bg-cyan-200 text-cyan-900 border-2 border-cyan-400";
              }
            } else if (blocked) {
              cellClass += readOnly ? "bg-red-50 text-red-400 cursor-default" : "bg-red-50 text-red-400 cursor-pointer hover:bg-red-100";
              statusIndicator = <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(239,68,68,0.1)_4px,rgba(239,68,68,0.1)_8px)] rounded-lg" />;
            } else {
              cellClass += readOnly 
                ? "bg-white border border-[#E7E5E4] cursor-default"
                : "bg-white hover:bg-[#F9F8F6] border border-[#E7E5E4] cursor-pointer hover:border-[#D4AF37] hover:shadow-md active:scale-95";
            }
            
            // Determina se questo è il giorno di CHECK-IN per mostrare nome+ospiti
            const isThisCheckInDay = booking && booking.check_in === dateStr;
            const checkInBooking = isThisCheckInDay ? booking : null;
            
            return (
              <div
                key={dateStr}
                data-date={dateStr}
                className={cellClass}
                onMouseDown={!past && !readOnly ? (e) => handleMouseDown(dateStr, e) : undefined}
                onMouseEnter={!past && !readOnly ? () => handleMouseEnter(dateStr) : undefined}
                onMouseUp={!past && !readOnly ? (e) => handleMouseUp(dateStr, e) : undefined}
                onClick={!past && readOnly ? () => handleClick(dateStr) : undefined}
                onTouchStart={!past ? (e) => handleTouchStart(dateStr, e) : undefined}
                title={hasOverlap ? `⚠️ Prenotazioni sovrapposte (${bookingsCount})` : (isClickableInReadOnly ? "Clicca per vedere i dettagli" : undefined)}
              >
                {statusIndicator}
                
                {/* INDICATORE OVERLAP - Bordino rosso tratteggiato discreto */}
                {hasOverlap && !past && (
                  <div 
                    className="absolute inset-0 rounded-lg border-2 border-dashed border-red-500 z-20 pointer-events-none"
                    style={{ borderSpacing: '4px' }}
                  />
                )}
                
                {/* INDICATORE OVERBOOKING - Bordino rosso tratteggiato + pallino arancione */}
                {hasOverbooking(dateStr) && !past && (
                  <>
                    {/* Bordo tratteggiato rosso perimetrale */}
                    <div 
                      className="absolute inset-0 rounded-lg border-2 border-dashed border-red-500 z-25 pointer-events-none"
                    />
                    {/* Pallino arancione pulsante in alto a destra */}
                    <div 
                      className="absolute top-1 right-1 z-40 bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow-lg animate-pulse"
                      title="⚠️ OVERBOOKING: Conflitto tra prenotazione sito e iCal"
                    >
                      !
                    </div>
                  </>
                )}
                
                {/* TRIANGOLO VIOLA CHECK-OUT - Priorità massima, sempre visibile
                    z-30 per essere SOPRA tutto (overlap indicator, booking colors, etc) */}
                {isCheckOut && (
                  <div 
                    className={`absolute top-0 left-0 w-0 h-0 border-t-[25px] border-t-violet-600 border-r-[25px] border-r-transparent z-30 ${past ? 'opacity-50' : ''}`}
                    title={`Check-out${getCheckOutCount(dateStr) > 1 ? ` (${getCheckOutCount(dateStr)} prenotazioni)` : ''}`}
                  />
                )}
                
                {/* Numero del giorno */}
                <span className="font-semibold text-base md:text-lg relative z-10">{d.day}</span>
                
                {/* Prezzo - mostrato solo se non c'è prenotazione e NON in readOnly (calendario cleaning) */}
                {price && !booking && !readOnly && (
                  <span className={`text-xs md:text-sm font-medium relative z-10 ${past ? 'text-gray-400' : blocked ? 'text-[#78716C]' : 'text-[#D4AF37]'}`}>
                    €{price}
                  </span>
                )}
                
                {/* Info prenotazione - SOLO nel giorno di CHECK-IN */}
                {checkInBooking && isICalBooking(checkInBooking) ? (
                  /* iCal: Mostra il nome della piattaforma e "Note" se presente */
                  <div className="flex flex-col items-center text-center relative z-10 gap-0.5 w-full overflow-hidden px-1">
                    <span className="text-[9px] md:text-[10px] truncate w-full font-medium leading-tight">
                      {checkInBooking.name || checkInBooking.platform_name || "iCal"}
                    </span>
                    {hasNotes && (
                      <span className="text-[8px] md:text-[9px] bg-white/30 px-1 rounded text-white font-medium">
                        Note
                      </span>
                    )}
                  </div>
                ) : checkInBooking && checkInBooking.status === "paid" ? (
                  /* PAID: Admin vede Nome/(ospiti)/€importo - Cleaning vede Nome/(ospiti) senza importo */
                  <div className="flex flex-col items-center text-center relative z-10 gap-0 w-full overflow-hidden px-1">
                    <span className="text-[10px] md:text-xs truncate w-full font-medium leading-tight">
                      {checkInBooking.name || ""}
                    </span>
                    <span className="text-[8px] md:text-[10px] leading-tight">
                      ({(checkInBooking.adults || 0) + (checkInBooking.children || 0) + (checkInBooking.infants || 0)})
                    </span>
                    {/* Importo solo se NON in readOnly (nascosto nel calendario cleaning) */}
                    {!readOnly && checkInBooking.grand_total > 0 && (
                      <span className="text-[8px] md:text-[10px] font-semibold leading-tight">
                        €{Math.round(checkInBooking.grand_total)}
                      </span>
                    )}
                    {hasNotes && (
                      <span className="text-[7px] md:text-[8px] bg-white/40 px-1 rounded text-green-900 font-semibold leading-tight">
                        Note
                      </span>
                    )}
                  </div>
                ) : checkInBooking ? (
                  /* CONFIRMED/Altri: 
                     - Admin: formato completo (nome + ospiti)
                     - Cleaning (readOnly): mostra il NOME inserito manualmente (es. "Danilo (3)") */
                  readOnly ? (
                    /* Calendario Cleaning: mostra il NOME della prenotazione (inserito manualmente) */
                    <div className="flex flex-col items-center text-center relative z-10 gap-0.5 w-full overflow-hidden px-1">
                      {checkInBooking.name && (
                        <span className="text-[9px] md:text-[10px] text-cyan-800 truncate w-full font-medium leading-tight" title={checkInBooking.name}>
                          {checkInBooking.name}
                        </span>
                      )}
                      {hasNotes && (
                        <span className="text-[8px] md:text-[9px] bg-cyan-600/30 px-1 rounded text-cyan-900 font-medium">
                          Note
                        </span>
                      )}
                    </div>
                  ) : (
                    /* Calendario Admin: formato completo */
                    <div className="flex flex-col items-center text-center relative z-10 gap-0.5 w-full overflow-hidden px-1">
                      <span className="text-[9px] md:text-[10px] truncate w-full font-medium leading-tight">
                        {formatBookingLabel(checkInBooking)}
                      </span>
                      {hasNotes && (
                        <span className="text-[8px] md:text-[9px] bg-cyan-600/30 px-1 rounded text-cyan-900 font-medium">
                          Note
                        </span>
                      )}
                    </div>
                  )
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  return (
    <div 
      className="relative"
      onMouseLeave={() => {
        if (!isTouchInteraction.current && dragRef.current.isDragging) {
          dragRef.current.isDragging = false;
          dragRef.current.moved = false;
        }
      }}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Sticky Action Panel - Fixed at top for better visibility */}
      {!readOnly && (
        <div className={`fixed top-[50px] left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-2xl transition-all duration-300 ${showActionPanel && selectedDates.length > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8 pointer-events-none'}`}>
          <div className="bg-white border-2 border-[#D4AF37] rounded-xl shadow-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-heading text-lg text-[#1C1917]">
                {selectedDates.length} giorn{selectedDates.length === 1 ? 'o' : 'i'} selezionat{selectedDates.length === 1 ? 'o' : 'i'}
              </span>
              <Button variant="ghost" size="sm" onClick={clearSelection} className="text-[#78716C] hover:text-red-600">
                <X size={18} /> Chiudi
              </Button>
            </div>
            
            {/* Price Input */}
            <div className="flex flex-wrap items-center gap-2 p-3 bg-[#F9F8F6] rounded-lg mb-3">
              <DollarSign size={18} className="text-[#D4AF37]" />
              <span className="text-sm font-medium">Prezzo:</span>
              <div className="flex items-center gap-1">
                <span className="text-[#78716C]">€</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder="180"
                  className="w-20 h-9"
                />
              </div>
              <Button onClick={applyPrice} className="bg-[#D4AF37] hover:bg-[#B8962E] text-white h-9" disabled={!priceInput}>
                Applica
              </Button>
            </div>
            
            {/* Info Banners */}
            {hasRealBookingsInSelection && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800 mb-3">
                <strong>⚠️</strong> Selezione include {bookingsInSelection.size} prenotazion{bookingsInSelection.size === 1 ? 'e' : 'i'} attiv{bookingsInSelection.size === 1 ? 'a' : 'e'}.
              </div>
            )}
            
            {hasManualConfirmationsInSelection && !hasRealBookingsInSelection && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs text-green-800 mb-3">
                <strong>✓</strong> Selezione include {manualConfirmationsInSelection.size} conferma manuale.
              </div>
            )}
            
            {/* Input per nome conferma - mostrato solo se ci sono date disponibili */}
            {hasAvailableInSelection && onConfirmDates && (
              <div className="flex items-center gap-2 mb-3">
                <Input
                  type="text"
                  value={confirmNameInput}
                  onChange={(e) => setConfirmNameInput(e.target.value)}
                  placeholder="Nome (ospiti) es: Danilo (3)"
                  className="flex-1 h-9 text-sm"
                />
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {hasAvailableInSelection && (
                <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50" onClick={blockSelected}>
                  <Lock size={14} className="mr-1" /> Blocca
                </Button>
              )}
              {hasManuallyBlockedInSelection && (
                <Button variant="outline" size="sm" className="border-green-300 text-green-600 hover:bg-green-50" onClick={unblockSelected}>
                  <Unlock size={14} className="mr-1" /> Sblocca
                </Button>
              )}
              {hasAvailableInSelection && onConfirmDates && (
                <Button variant="outline" size="sm" className="border-green-400 text-green-700 hover:bg-green-50" onClick={confirmSelected} disabled={!confirmNameInput.trim()}>
                  <Check size={14} className="mr-1" /> Conferma
                </Button>
              )}
              {/* Pulsante Dettagli - solo se c'è esattamente 1 prenotazione selezionata */}
              {allBookingsForNotes.size === 1 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-[rgb(80,80,80)] text-[rgb(80,80,80)] hover:bg-[rgb(80,80,80)]/10" 
                  onClick={() => {
                    const bookingId = Array.from(allBookingsForNotes)[0];
                    const booking = bookings.find(b => b.request_id === bookingId);
                    if (booking) setDetailsBooking(booking);
                  }}
                >
                  <Eye size={14} className="mr-1" /> Dettagli
                </Button>
              )}
              {/* Pulsante Note - solo se c'è esattamente 1 prenotazione */}
              {allBookingsForNotes.size === 1 && getAuthHeaders && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-amber-500 text-amber-700 hover:bg-amber-50" 
                  onClick={() => openNotesModal(selectedBookingForNotes)}
                >
                  <StickyNote size={14} className="mr-1" /> Note
                </Button>
              )}
              {/* Pulsante Rimuovi Note - solo se c'è una nota */}
              {allBookingsForNotes.size === 1 && selectedBookingForNotes?.notes && getAuthHeaders && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-red-300 text-red-600 hover:bg-red-50" 
                  onClick={deleteNotes}
                >
                  <Trash2 size={14} className="mr-1" /> Rimuovi note
                </Button>
              )}
              {/* Pulsante Gestisci Overbooking - solo se c'è overbooking nella selezione */}
              {hasOverbookingInSelection && onResolveOverbooking && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-orange-500 text-orange-700 hover:bg-orange-50 font-medium" 
                  onClick={() => onResolveOverbooking(selectedDates.filter(d => hasOverbooking(d)))}
                >
                  <AlertTriangle size={14} className="mr-1" /> Gestisci Overbooking
                </Button>
              )}
              {/* Pulsante Elimina - solo per booking non iCal */}
              {allBookingsInSelection.size > 0 && onCancelBooking && (
                <Button variant="outline" size="sm" className="border-red-500 text-red-700 hover:bg-red-100 font-medium" onClick={cancelBookingsInSelection}>
                  <Trash2 size={14} className="mr-1" /> Elimina
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm || (() => {})}
        title={confirmModal.title}
        message={confirmModal.message}
        details={confirmModal.details}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
      />
      
      {/* Year Navigation Header - Sticky */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm py-3 px-2 rounded-lg mb-4 border-b border-[#E7E5E4] shadow-sm">
        <div className="flex items-center justify-between">
          <Button 
            variant="outline"
            size="sm" 
            onClick={goToPrevYear}
            disabled={!canGoPrev}
            className={`touch-manipulation ${!canGoPrev ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <ChevronLeft size={18} /> {displayYear - 1}
          </Button>
          
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-[1.75rem] font-medium text-[#1C1917]">{displayYear}</h2>
            {displayYear !== currentYear && (
              <Button variant="ghost" size="sm" onClick={goToCurrentYear} className="text-[#D4AF37]">
                <Calendar size={16} className="mr-1" /> Oggi
              </Button>
            )}
            {selectedDates.length > 0 && (
              <span className="bg-[#D4AF37] text-white px-3 py-1 rounded-full text-sm font-medium">
                {selectedDates.length} sel.
              </span>
            )}
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={goToNextYear}
            disabled={!canGoNext}
            className={`touch-manipulation ${!canGoNext ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {displayYear + 1} <ChevronRight size={18} />
          </Button>
        </div>
      </div>
      
      {/* Legend - Con triangolo Check-out e iCal */}
      <div className="flex flex-wrap justify-center gap-4 text-xs mb-4 px-2">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-600 rounded" />
          <span className="text-[#78716C]">Pagato</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-cyan-200 border border-cyan-400 rounded" />
          <span className="text-[#78716C]">Confermato</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-gray-400 rounded" />
          <span className="text-[#78716C]">iCal Import</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-red-50 rounded relative overflow-hidden">
            <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_2px,rgba(239,68,68,0.2)_2px,rgba(239,68,68,0.2)_4px)]" />
          </div>
          <span className="text-[#78716C]">Bloccato</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-white rounded border-2 border-dashed border-red-500" />
          <span className="text-[#78716C]">Overbooking</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 relative">
            <div className="absolute top-0 left-0 w-0 h-0 border-t-[16px] border-t-violet-600 border-r-[16px] border-r-transparent" />
          </div>
          <span className="text-[#78716C]">Check-out</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-[#D4AF37] rounded" />
          <span className="text-[#78716C]">Selezionato</span>
        </div>
      </div>
      
      {/* 12 Months - VERTICAL LAYOUT (one per row) */}
      <div className="space-y-8">
        {Array.from({ length: 12 }, (_, i) => renderMonth(displayYear, i, i === 11))}
      </div>

      {/* Modal Dettagli Prenotazione */}
      {detailsBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setDetailsBooking(null)}>
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-[#E7E5E4] px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl text-[#1C1917]">Dettagli Prenotazione</h2>
                {readOnly && (
                  <p className="text-xs text-[#78716C]">Sola lettura</p>
                )}
              </div>
              <button 
                onClick={() => setDetailsBooking(null)}
                className="p-2 hover:bg-[#F9F8F6] rounded-full transition-colors"
              >
                <X size={20} className="text-[#78716C]" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              {/* Stato */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                  detailsBooking.status === "paid" ? "bg-green-100 text-green-800" :
                  detailsBooking.status === "confirmed" ? "bg-cyan-100 text-cyan-800" :
                  detailsBooking.status === "ical" || isICalBooking(detailsBooking) ? "bg-gray-200 text-gray-800" :
                  "bg-yellow-100 text-yellow-800"
                }`}>
                  {detailsBooking.status === "paid" ? "Paid" :
                   detailsBooking.status === "confirmed" ? "Confirmed" :
                   detailsBooking.status === "ical" || isICalBooking(detailsBooking) ? "iCal Import" :
                   detailsBooking.status}
                </span>
                {detailsBooking.booking_number && (
                  <span className="text-sm text-[#78716C]">#{detailsBooking.booking_number}</span>
                )}
              </div>

              {/* Nome Ospite */}
              <div>
                <p className="text-xs text-[#78716C] uppercase tracking-wide mb-1">
                  {isICalBooking(detailsBooking) ? "Piattaforma" : "Nome Ospite"}
                </p>
                {isICalBooking(detailsBooking) ? (
                  <p className="font-medium text-lg">{detailsBooking.name || detailsBooking.platform_name || "iCal"}</p>
                ) : detailsBooking.status === "confirmed" ? (
                  /* Per conferme manuali: mostra il contenuto del nome/messaggio */
                  <div className="bg-[#F9F8F6] rounded-lg p-3">
                    <p className="font-medium whitespace-pre-wrap">{detailsBooking.message || detailsBooking.name || "-"}</p>
                  </div>
                ) : (
                  /* Per prenotazioni paid/altre: mostra il nome ospite */
                  <p className="font-medium text-lg">{detailsBooking.name || "-"}</p>
                )}
              </div>

              {/* Numero Ospiti - nascondi per iCal */}
              {!isICalBooking(detailsBooking) && (
                <div>
                  <p className="text-xs text-[#78716C] uppercase tracking-wide mb-1">Numero Ospiti</p>
                  <div className="flex items-center gap-4">
                    <span className="text-sm">
                      <strong>{detailsBooking.adults || 0}</strong> Adult{(detailsBooking.adults || 0) !== 1 ? 'i' : 'o'}
                    </span>
                    {(detailsBooking.children || 0) > 0 && (
                      <span className="text-sm">
                        <strong>{detailsBooking.children}</strong> Bambin{detailsBooking.children !== 1 ? 'i' : 'o'}
                      </span>
                    )}
                    {(detailsBooking.infants || 0) > 0 && (
                      <span className="text-sm">
                        <strong>{detailsBooking.infants}</strong> Infant{detailsBooking.infants !== 1 ? 'i' : 'e'}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Date Soggiorno */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#78716C] uppercase tracking-wide mb-1">Check-in</p>
                  <p className="font-medium">{detailsBooking.check_in || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-[#78716C] uppercase tracking-wide mb-1">Check-out</p>
                  <p className="font-medium">{detailsBooking.check_out || "-"}</p>
                </div>
              </div>
              
              {/* Numero Notti */}
              {detailsBooking.check_in && detailsBooking.check_out && (
                <div className="bg-[#F9F8F6] rounded-lg p-3 flex items-center justify-center gap-2">
                  <Calendar size={18} className="text-[#D4AF37]" />
                  <span className="text-lg font-semibold text-[#1C1917]">
                    {(() => {
                      const checkIn = new Date(detailsBooking.check_in);
                      const checkOut = new Date(detailsBooking.check_out);
                      const nights = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                      return `${nights} nott${nights === 1 ? 'e' : 'i'}`;
                    })()}
                  </span>
                </div>
              )}

              {/* Contatti (se presenti) - nascondi per iCal */}
              {!isICalBooking(detailsBooking) && (detailsBooking.email || detailsBooking.phone) && (
                <div>
                  <p className="text-xs text-[#78716C] uppercase tracking-wide mb-1">Contatti</p>
                  {detailsBooking.email && <p className="text-sm">{detailsBooking.email}</p>}
                  {detailsBooking.phone && <p className="text-sm text-[#78716C]">{detailsBooking.phone}</p>}
                </div>
              )}

              {/* Note - campo SEMPRE visibile e BEN EVIDENZIATO */}
              <div className="border-t border-[#E7E5E4] pt-5">
                <p className="text-sm text-amber-700 uppercase tracking-wide mb-2 flex items-center gap-2 font-semibold">
                  <StickyNote size={16} /> Note
                </p>
                {detailsBooking.notes ? (
                  <div className="bg-amber-100 border-2 border-amber-300 rounded-lg p-4 shadow-sm">
                    <p className="text-base whitespace-pre-wrap text-amber-900 font-bold">{detailsBooking.notes}</p>
                  </div>
                ) : (
                  <p className="text-sm text-[#78716C] italic">Nessuna nota</p>
                )}
              </div>

              {/* Messaggio - solo per prenotazioni NON confermate manualmente e NON iCal */}
              {detailsBooking.status !== "confirmed" && !isICalBooking(detailsBooking) && (
                <div className="border-t border-[#E7E5E4] pt-5">
                  <p className="text-xs text-[#78716C] uppercase tracking-wide mb-2">Messaggio</p>
                  {detailsBooking.message ? (
                    <div className="bg-[#F9F8F6] rounded-lg p-4">
                      <p className="text-sm whitespace-pre-wrap">{detailsBooking.message}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-[#78716C] italic">No message</p>
                  )}
                </div>
              )}

              {/* Importo (solo per paid) */}
              {detailsBooking.status === "paid" && detailsBooking.grand_total > 0 && (
                <div className="border-t border-[#E7E5E4] pt-5">
                  <p className="text-xs text-[#78716C] uppercase tracking-wide mb-1">Totale Pagato</p>
                  <p className="font-heading text-2xl text-green-700">€{Math.round(detailsBooking.grand_total)}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-[#E7E5E4] px-6 py-4">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setDetailsBooking(null)}
              >
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal per Modifica Note */}
      {showNotesModal && selectedBookingForNotes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowNotesModal(false)}>
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="border-b border-[#E7E5E4] px-6 py-4 flex items-center justify-between">
              <h2 className="font-heading text-xl text-[#1C1917] flex items-center gap-2">
                <StickyNote size={20} className="text-amber-600" />
                Note
              </h2>
              <button 
                onClick={() => setShowNotesModal(false)}
                className="p-2 hover:bg-[#F9F8F6] rounded-full transition-colors"
              >
                <X size={20} className="text-[#78716C]" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-[#78716C] mb-2">
                  {isICalBooking(selectedBookingForNotes) 
                    ? `${selectedBookingForNotes.name || selectedBookingForNotes.platform_name || "iCal"} (${selectedBookingForNotes.check_in} - ${selectedBookingForNotes.check_out})`
                    : `${selectedBookingForNotes.name || "Prenotazione"} (${selectedBookingForNotes.check_in} - ${selectedBookingForNotes.check_out})`
                  }
                </p>
                <Textarea
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  placeholder="Inserisci note per questa prenotazione..."
                  className="min-h-[120px] resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-[#E7E5E4] px-6 py-4 flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowNotesModal(false)}
              >
                Annulla
              </Button>
              <Button 
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                onClick={saveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? "Salvataggio..." : "Salva Note"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
